import { useEffect, useState } from "react";
import { Landmark, CreditCard, ShieldCheck, ArrowUpDown } from "lucide-react";
import Header from "@/components/Header";
import DepositForm, { DepositHistory } from "@/components/DepositForm";
import { api } from "@/lib/api";
import { toast } from "sonner";

function CopyRow({ icon: Icon, label, value, testId }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label} copied`);
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      className="w-full flex items-center gap-3 rounded-xl border border-white/15 bg-white/10 backdrop-blur px-4 py-3 text-left hover:bg-white/15 transition-colors"
    >
      <div className="h-10 w-10 rounded-lg bg-white text-red-700 flex items-center justify-center shrink-0">
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-widest text-white/80 font-semibold">{label}</div>
        <div className="text-white font-bold text-base sm:text-lg truncate">{value}</div>
      </div>
    </button>
  );
}

export default function FundDepositBank() {
  const [bank, setBank] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/deposits/bank-info").then((r) => setBank(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <div style={{ background: "linear-gradient(180deg, #7f1d1d 0%, #991b1b 100%)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 text-center text-white">
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Fund Deposit
          </h1>
          <p className="text-white/85 mt-1">Bank Deposit</p>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 -mt-6 pb-14 space-y-5">
        {/* Bank card */}
        <div
          className="rounded-2xl p-5 shadow-xl relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #991b1b 0%, #7f1d1d 50%, #450a0a 100%)" }}
        >
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-white/90 mb-4">
              <div className="h-9 w-9 rounded-full bg-white flex items-center justify-center text-blue-900">
                <Landmark size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/70">Bank</div>
                <div className="text-xl font-extrabold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {bank?.bank_name || "State Bank of India"}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <CopyRow
                icon={Landmark}
                label="Bank Account Holder Name"
                value={bank?.account_holder || "ISHIKA SMART OFFER PRIVATE LIMITED"}
                testId="bank-copy-holder"
              />
              <CopyRow
                icon={CreditCard}
                label="Bank Account Number"
                value={bank?.account_number || "45305826200"}
                testId="bank-copy-account"
              />
              <CopyRow
                icon={ShieldCheck}
                label="IFSC Code"
                value={bank?.ifsc || "SBIN0001228"}
                testId="bank-copy-ifsc"
              />
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
          <ArrowUpDown size={22} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-amber-800 text-sm">Note:</div>
            <p className="text-xs text-amber-800 leading-relaxed mt-1">
              फंड लेने के लिए <span className="font-bold">Deposit Request</span> और
              निकासी करने के लिए <span className="font-bold">Withdrawal Request</span> दोनों रिक्वेस्ट लगा सकते हैं।
            </p>
          </div>
        </div>

        <DepositForm method="bank" onSubmitted={() => setReloadKey((k) => k + 1)} />
        <DepositHistory reloadKey={reloadKey} />
      </main>
    </div>
  );
}
